export const createChartPlugins = (callAnalytics: any) => [
    {
      id: "centerText",
      beforeDraw: function (chart: any) {        
        const width = chart.width;
        const height = chart.height;
        const ctx = chart.ctx;
        ctx.restore();
        ctx.font = "600 24px 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
        ctx.textBaseline = "middle";
        ctx.textAlign = "center";
        const textX = width / 2;
        const textY = height / 2.3;
        ctx.fillText(callAnalytics.totalCalls, textX, textY);
        ctx.save();
      },
    },
    {
      id: "customLabelsAndLines",
      afterDraw: function (chart: any) {
        const ctx = chart.ctx;
        const datasets = chart.data.datasets[0];
        const meta = chart.getDatasetMeta(0);
        const centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
        const centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2;
  
        ctx.save();
        ctx.lineWidth = 1;
        ctx.font = "500 14px 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
  
        meta.data.forEach((element: any, index: number) => {
          const label = chart.data.labels[index];
          const value = datasets.data[index];
          const color = datasets.backgroundColor[index];
  
          // Calculate the middle angle of the segment
          const midAngle = (element.startAngle + element.endAngle) / 2;
  
          // Calculate line points from the middle of the segment
          const radius = element.outerRadius;
          const labelRadius = radius * 1.1;
  
          const startX = centerX + Math.cos(midAngle) * radius;
          const startY = centerY + Math.sin(midAngle) * radius;
  
          const endX = centerX + Math.cos(midAngle) * labelRadius;
          const endY = centerY + Math.sin(midAngle) * labelRadius;
  
          // Set stroke color and draw initial line
          ctx.strokeStyle = "#000000";
          ctx.beginPath();
          ctx.moveTo(startX, startY);
          ctx.lineTo(endX, endY);
  
          // Determine direction for horizontal line
          const isRightSide = endX > centerX;
          const horizontalLength = isRightSide ? 140 : -140;
          
          // Draw horizontal line
          ctx.lineTo(endX + horizontalLength, endY);
          ctx.stroke();
  
          // Define spacing for label and percentage
          const verticalSpacing = 3;
          const labelPadding = 0;
  
          const labelX = endX + (isRightSide ? horizontalLength + labelPadding : horizontalLength - labelPadding);
  
          // Draw text label and percentage
          ctx.textAlign = isRightSide ? "right" : "left";
          ctx.textBaseline = "bottom"; // Change to position text above line
          
          // Draw label (using default dark color)
          ctx.fillStyle = "#344054";
          ctx.fillText(label, labelX, endY - verticalSpacing);
  
          // Draw percentage in matching color
          ctx.fillStyle = color;
          ctx.textBaseline = "top"; // Change to position percentage below line
          ctx.fillText(`${value}%`, labelX, endY + verticalSpacing/2);
  
          // Draw extended horizontal line
          const textWidth = Math.max(
            ctx.measureText(label).width,
            ctx.measureText(`${value}%`).width
          );
          
          ctx.beginPath();
          if (isRightSide) {
            ctx.moveTo(labelX, endY);
            ctx.lineTo(labelX + textWidth + 20, endY);
          } else {
            ctx.moveTo(labelX, endY);
            ctx.lineTo(labelX - textWidth - 20, endY);
          }
          ctx.stroke();
        });
  
        ctx.restore();
      },
    },
  ];

export const pieOptions: any = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "75%",
  radius: "80%",
  layout: {
    padding: {
      top: 20,
      right: 120,
      bottom: 20,
      left: 120,
    },
  },
  plugins: {
    legend: {
      display: true,
      position: "bottom" as const,
      labels: {
        usePointStyle: true,
        padding: 20,
        font: {
          size: 12,
          family: "'Inter', sans-serif",
        },
        color: "#667085",
      },
    },
    tooltip: {
      enabled: false,
    },
    datalabels: {
      display: false, 
    },
  },
};

  // Sample data
  export const sentimentData = (callAnalytics: any) => {
    // Prepare data and labels
    const rawLabels = [
      "Positive",
      "Slightly Positive",
      "Neutral",
      "Slightly Negative",
      "Negative",
    ];
    const rawData = [
      callAnalytics?.distribution?.positive || 0,
      callAnalytics?.distribution?.slightlyPositive || 0,
      callAnalytics?.distribution?.neutral || 0,
      callAnalytics?.distribution?.slightlyNegative || 0,
      callAnalytics?.distribution?.negative || 0,
    ];
    const backgroundColors = [
      "#4CAF50",  // Positive
      "#8BC34A",  // Slightly Positive
      "#9E9E9E",  // Neutral
      "#FFC107",  // Slightly Negative
      "#F44336",  // Negative
    ];
  
    // Filter out labels, data, and colors where data is zero
    const filteredData = rawData
      .map((value, index) => ({ value, label: rawLabels[index], color: backgroundColors[index] }))
      .filter(item => item.value > 0);
  
    return {
      labels: filteredData.map(item => item.label),
      datasets: [
        {
          data: filteredData.map(item => item.value),
          backgroundColor: filteredData.map(item => item.color),
          borderWidth: 0,
          spacing: 2,
          hoverOffset: 9,
        },
      ],
    };
  };
  

  export const getBarOptions = (data: number[]): any => {
    const maxValue = Math.max(...data);
    
    // First, determine the magnitude of the max value
    const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
    
    // Round up to a clean number based on magnitude
    let roundedMax;
    if (maxValue <= 100) {
        roundedMax = Math.ceil(maxValue / 10) * 10; // Round to nearest 10
    } else if (maxValue <= 1000) {
        roundedMax = Math.ceil(maxValue / 100) * 100; // Round to nearest 100
    } else if (maxValue <= 10000) {
        roundedMax = Math.ceil(maxValue / 1000) * 1000; // Round to nearest 1000
    } else {
        roundedMax = Math.ceil(maxValue / (magnitude / 2)) * (magnitude / 2); // Round to significant digit
    }
    
    // Calculate step size based on rounded max to get clean intervals
    // Aim for 5-7 tick marks
    const divisions = 5;
    const stepSize = Math.ceil(roundedMax / divisions / (magnitude / 10)) * (magnitude / 10);
    
    return {
        plugins: {
            legend: {
                display: false,
            },
            datalabels: {
                display: false,
            },
        },
        responsive: true,
        maintainAspectRatio: false,
        barThickness: 70,
        scales: {
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    font: {
                        size: 8,
                        weight: "bold",
                    },
                    color: "#000",
                },
                border: {
                    display: true,
                    color: "#000",
                    width: 1,
                },
            },
            y: {
                min: 0,
                max: roundedMax,
                ticks: {
                    stepSize: stepSize,
                    // Force ticks to be integers without decimal places
                    callback: function(value:any) {
                        return Number.isInteger(value) ? value : null;
                    },
                    font: {
                        size: 12,
                        weight: "bold",
                    },
                    color: "#000",
                },
                grid: {
                    color: "#E5E5E5",
                    lineWidth: 1,
                    borderDash: [5, 5],
                },
                border: {
                    display: true,
                    color: "#000",
                    width: 1,
                },
                title: {
                    display: true,
                    text: "No. of Topics",
                    font: {
                        size: 14,
                        weight: "bold",
                    },
                    padding: {
                        bottom: 10,
                    },
                    color: "#000",
                },
            },
        },
    };
};


export const rangeOptions = [
  { value: "Day", label: "Day" },
  { value: "Week", label: "Week" },
  { value: "Month", label: "Month" },
  { value: "Year", label: "Year" },
];

export const sentimentScaleDefinition = {
  "-1": "Negative",
  "0": "Neutral",
  "1": "Positive",
};

export  const topicsDatas = {
  labels: [
    "Workplace Related",
    "Mental Health Related",
    "Physical Health",
    "Financial Insecurity",
    "Housing Insecurity",
    "Food Insecurity",
    "Other",
  ],
    datasets: [
      {
        data: [480, 380, 280, 240, 160, 480, 380],
        backgroundColor: "#673AB7",
      },
    ],
  };


  export const SENTIMENT_MAPPING = {
    Positive: [1],
    Neutral: [0],
    Negative: [-1],
  } as const;

  export const standardColors = [
    "#673AB7", "#FF9800", "#E91E63", "#4CAF50", "#2196F3", 
    "#FFC107", "#9C27B0", "#F44336", "#00BCD4", "#8BC34A"
  ];